import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { FiEdit, FiPlus } from 'react-icons/fi';
// eslint-disable-next-line no-unused-vars
import { useAuth } from '../contexts/AuthContext';
import ConditionalButton from '../components/common/ConditionalButton';

const fields = [
  { name: 'denomination', label: 'Dénomination' },
  { name: 'adresse', label: 'Adresse' },
  { name: 'telfix1', label: 'Tel Fixe 1' },
  { name: 'telfix2', label: 'Tel Fixe 2' },
  { name: 'telmob1', label: 'Tel Mobile 1' },
  { name: 'telmob2', label: 'Tel Mobile 2' },
  { name: 'email', label: 'Email' },
  { name: 'siteweb', label: 'Site web' },
  { name: 'bic', label: 'BIC' },
  { name: 'ncp', label: 'N° Compte' },
  { name: 'logo', label: 'Logo (URL)' },
  { name: 'civilite_responsable', label: 'Civilité Responsable' },
  { name: 'nom_responsable', label: 'Nom Responsable' },
  { name: 'prenom_responsable', label: 'Prénom Responsable' },
  { name: 'titre_responsable', label: 'Titre Responsable' },
  { name: 'ville', label: 'Ville' },
];

const emptyForm = Object.fromEntries(fields.map(f => [f.name, '']));

const EtablissementPage = () => {
 // const { user } = useAuth();
  const [etab, setEtab] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const fetchEtab = async () => {
    setLoading(true);
    try {
      const res = await api.get('/etablissement');
      setEtab(res.data.data || null);
    } catch {
      setEtab(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEtab();
  }, []);

  const handleEdit = () => {
    setForm(etab ? { ...etab } : emptyForm);
    setModalOpen(true);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    try {
      // Vérifier s'il y a un fichier logo à uploader
      const hasLogoFile = form.logo instanceof File;
      
      if (hasLogoFile) {
        // Si upload de logo : utiliser FormData
        const formData = new FormData();
        fields.forEach(field => {
          if (field.name === 'logo') {
            formData.append('logo', form.logo);
          } else {
            const value = form[field.name];
            formData.append(field.name, value !== null && value !== undefined ? value : '');
          }
        });
        
        if (etab && etab.id) {
          await api.put(`/etablissement/${etab.id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          await api.post('/etablissement', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      } else {
        // Pas de logo : utiliser JSON (plus simple et fiable)
        const data = {};
        fields.forEach(field => {
          if (field.name !== 'logo') {
            const value = form[field.name];
            data[field.name] = value !== null && value !== undefined ? value : '';
          }
        });
        
        if (etab && etab.id) {
          await api.put(`/etablissement/${etab.id}`, data);
        } else {
          await api.post('/etablissement', data);
        }
      }
      
      setModalOpen(false);
      fetchEtab();
      Swal.fire('Succès', "Les informations de l'établissement ont été enregistrées.", 'success');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      Swal.fire('Erreur', "Erreur lors de l'enregistrement.", 'error');
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Informations de l'établissement</h1>
        <ConditionalButton
          entity="etablissement"
          permission={etab ? "update" : "create"}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center gap-2"
          onClick={handleEdit}
        >
          {etab ? <><FiEdit /> Modifier</> : <><FiPlus /> Ajouter</>}
        </ConditionalButton>
      </div>
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        {etab ? (
          <>
            {/* Logo affiché en premier, centré dans un rond */}
            {etab.logo && (
              <div className="mb-6 flex justify-center">
                <div className="w-32 h-32 rounded-full border-4 border-primary-200 overflow-hidden bg-gray-100 shadow-lg flex items-center justify-center">
                  <img 
                    src={`/uploads/${etab.logo}`}
                    alt="Logo établissement" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}
            {/* Autres informations en grille */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.filter(f => f.name !== 'logo').map(f => (
                <div key={f.name} className="mb-2">
                  <div className="text-xs text-gray-500">{f.label}</div>
                  <div className="font-medium">{etab[f.name] || <span className="text-gray-300">-</span>}</div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-gray-500 italic">Aucune information enregistrée.</div>
        )}
      </div>
      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">{etab ? 'Modifier' : 'Ajouter'} les informations de l'établissement</h2>
            <form onSubmit={handleModalSubmit}>
              {/* Logo sur la première ligne, centré dans un rond compact */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-center">Logo de l'établissement</label>
                <div className="flex justify-center items-center">
                  <div className="relative">
                    {/* Afficher le logo actuel ou un placeholder */}
                    <div className="w-24 h-24 rounded-full border-2 border-gray-200 overflow-hidden bg-gray-100 flex items-center justify-center shadow-sm">
                      {form.logo instanceof File ? (
                        <img 
                          src={URL.createObjectURL(form.logo)} 
                          alt="Nouveau logo" 
                          className="w-full h-full object-cover"
                        />
                      ) : etab?.logo ? (
                        <img 
                          src={`/uploads/${etab.logo}`}
                          alt="Logo établissement" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      {(!form.logo || (!(form.logo instanceof File) && !etab?.logo)) && (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {/* Input file stylisé */}
                    <label className="absolute inset-0 cursor-pointer flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-50 rounded-full">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.svg,.gif"
                        onChange={e => {
                          const file = e.target.files[0];
                          if (file) {
                            setForm({ ...form, logo: file });
                          }
                        }}
                        className="hidden"
                      />
                      <span className="text-white text-xs font-medium">Changer</span>
                    </label>
                  </div>
                </div>
                {/* Input file visible pour faciliter l'utilisation */}
                <div className="mt-2 flex justify-center">
                  <label className="cursor-pointer px-3 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700 transition text-xs">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.svg,.gif"
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file) {
                          setForm({ ...form, logo: file });
                        }
                      }}
                      className="hidden"
                    />
                    {form.logo instanceof File ? 'Changer le logo' : 'Sélectionner un logo'}
                  </label>
                </div>
              </div>

              {/* Champs en grille 4 colonnes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {fields.filter(f => f.name !== 'logo').map(f => (
                  <div key={f.name}>
                    <label className="block text-xs font-medium mb-1">{f.label}</label>
                    <input
                      type="text"
                      name={f.name}
                      value={form[f.name] || ''}
                      onChange={e => setForm({ ...form, [f.name]: e.target.value })}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm">Annuler</button>
                <ConditionalButton
                  entity="etablissement"
                  permission={etab ? "update" : "create"}
                  type="submit"
                  className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 text-sm"
                >
                  Enregistrer
                </ConditionalButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EtablissementPage;
